import React, { useState, useEffect } from 'react';
import { arrayMove, insertAtIndex } from '#lib/utils/array.js';
import { set } from 'lodash-es';

const ACTIONS = {
    REPLACE_STATE: 'replace_state',
    ADD_PLACE_FILTER: 'add_place_filter',
    DELETE_PLACE_FILTER: 'delete_place_filter',
    MOVE_ITEM: 'move_item',
    DELETE_ITEM: 'delete_item',
    EDIT_FILTER: 'edit_filter',
    EDIT_MACRO_FILTER: 'edit_macro_filter',
    EDIT_PLACE_FILTER: 'edit_place',
    GET_CITIES: 'get_cities',
    SELECT_PLACE_FILTER: 'select_place_filter'
};

const makeEmptyPlaceFilter = (name) => ({
    name: name ?? '',
    filters: [],
    regenerate: true,
    empty: true
});

function reducer(state, action) {
    const insertPlaceFilter = (stateModel, name) => {
        const index = stateModel.length;
        const nameExists = stateModel.some((place) => place.name.trim().toLowerCase() === name?.trim()?.toLowerCase());
        let newPlaces = nameExists ? stateModel : insertAtIndex(stateModel, index, makeEmptyPlaceFilter(name));
        const emptyPlaceIndex = newPlaces.findIndex((place) => place.name !== '');
        let filteredPlaces = newPlaces;
        if (emptyPlaceIndex) filteredPlaces = newPlaces.splice(emptyPlaceIndex, 1);
        return [...filteredPlaces];
    };

    switch (action.type) {
        case ACTIONS.REPLACE_STATE: {
            return [...action.payload];
        }
        case ACTIONS.ADD_PLACE_FILTER: {
            if (!state) {
                return [makeEmptyPlaceFilter()];
            }
            return insertPlaceFilter(state);
        }
        case ACTIONS.DELETE_PLACE_FILTER: {
            const { name, index } = action.payload;
            const updated = structuredClone(state);

            const placeIndex = updated.findIndex((place) => place.name === name) ?? index;
            if (updated.length <= 1) updated.push(makeEmptyPlaceFilter());
            updated.splice(placeIndex, 1);
            return updated;
        }
        case ACTIONS.MOVE_ITEM: {
            const { placeIndex, from, to } = action.payload;
            const updated = structuredClone(state);
            const place = updated[placeIndex];
            place.filters = arrayMove(place.filters, from, to);
            return updated;
        }
        case ACTIONS.DELETE_ITEM: {
            const { placeIndex, id } = action.payload;
            const updated = structuredClone(state);
            const place = updated[placeIndex];
            place.filters = place.filters.filter((filterId) => filterId !== id);
            return updated;
        }
        case ACTIONS.EDIT_FILTER: {
            const { placeIndex, type, filterId, filterName } = action.payload;
            const updated = structuredClone(state);
            const place = updated[placeIndex];
            const filter = {
                id: filterId,
                name: filterName,
                type: type
            };
            const newFilters = place.filters.some((filter) => filter.id === filterId)
                ? place.filters?.filter((f) => f.id !== filterId)
                : [...place.filters, filter];
            set(place, 'filters', newFilters);
            return updated;
        }
        case ACTIONS.EDIT_MACRO_FILTER: {
            const { totalFilters, placeIndex, type, value } = action.payload;
            const updated = structuredClone(state);
            const place = updated[placeIndex];
            let newFilters = [];
            if (value) {
                const idActiveFilters = place.filters.map((filter) => filter.id);
                const addedFilters = totalFilters.filter(
                    (filter) => filter.type === type && !idActiveFilters.includes(filter.id)
                );
                newFilters = [
                    ...place.filters,
                    ...addedFilters.map((filter) => ({ id: filter.id, name: filter.name, type: filter.type }))
                ];
                set(place, 'filters', newFilters);
            } else {
                newFilters = place.filters.filter((filter) => filter.type !== type);
                set(place, 'filters', newFilters);
            }
            return updated;
        }
        case ACTIONS.EDIT_PLACE_FILTER: {
            const { at, field, data } = action.payload;
            const updated = structuredClone(state);
            const place = updated[at];
            if (field === 'name') {
                if (!place.regenerate) place.regenerate = true;
                if (!place.empty) place.empty = true;
            }
            set(place, field, data);
            return updated;
        }
        case ACTIONS.GET_CITIES: {
            const { places, planCities } = action.payload;
            let updated = structuredClone(state);
            const placeCities = places.map((place) => place?.name).filter((el) => el);
            const cities = new Set(planCities.concat(placeCities));
            cities.forEach((city) => {
                updated = insertPlaceFilter(updated, city);
            });
            // setto i flag empty e regenerate per i places non vuoti
            places.forEach((location) => {
                const hasNoPlaces = location.info.length === 0 && location.suggestions.length === 0;
                const placeFilterIndex = updated.findIndex(
                    (el) => el.name.trim().toLowerCase() === location.name.trim().toLowerCase()
                );
                updated[placeFilterIndex].empty = hasNoPlaces;
                updated[placeFilterIndex].regenerate = hasNoPlaces;
            });
            return updated;
        }
    }
}

export function usePlaceFilters(initialState, init) {
    const [state, dispatch] = React.useReducer(reducer, initialState, init);
    const [selectedPlaceFilterIndex, setSelectedPlaceFilterIndex] = useState(0);

    useEffect(() => {
        const places = initialState ?? [makeEmptyPlaceFilter()];
        dispatch({ type: ACTIONS.REPLACE_STATE, payload: places });
    }, [init, initialState]);

    const addPlaceFilter = React.useCallback(() => {
        dispatch({
            type: ACTIONS.ADD_PLACE_FILTER
        });
    }, []);

    const deletePlaceFilter = React.useCallback((name, index) => {
        dispatch({
            type: ACTIONS.DELETE_PLACE_FILTER,
            payload: { name, index }
        });
    }, []);

    const moveItem = React.useCallback((placeIndex, from, to) => {
        dispatch({ type: ACTIONS.MOVE_ITEM, payload: { placeIndex, from, to } });
    }, []);

    const deleteItem = React.useCallback((placeIndex, id) => {
        dispatch({
            type: ACTIONS.DELETE_ITEM,
            payload: { placeIndex, id }
        });
    }, []);

    const editFilter = React.useCallback((placeIndex, type, filterId, filterName) => {
        dispatch({
            type: ACTIONS.EDIT_FILTER,
            payload: { placeIndex, type, filterId, filterName }
        });
    }, []);

    const editMacroFilter = React.useCallback((totalFilters, placeIndex, type, value) => {
        dispatch({
            type: ACTIONS.EDIT_MACRO_FILTER,
            payload: { totalFilters, placeIndex, type, value }
        });
    }, []);

    const editPlaceFilter = React.useCallback((at, field, data) => {
        dispatch({
            type: ACTIONS.EDIT_PLACE_FILTER,
            payload: { at, field, data }
        });
    }, []);

    const getCities = React.useCallback((places, planCities) => {
        dispatch({
            type: ACTIONS.GET_CITIES,
            payload: { places, planCities }
        });
    }, []);

    return {
        initialState,
        placeFilters: state,
        addPlaceFilter,
        selectedPlaceFilterIndex,
        setSelectedPlaceFilterIndex,
        editPlaceFilter,
        deletePlaceFilter,
        moveItem,
        deleteItem,
        getCities,
        editFilter,
        editMacroFilter
    };
}
